// Race engine: pure logic (no DOM), used by the solo client and by the multiplayer server.
// Emitted events are structured ({ type, who, src, ... }): each client words them from its own point of view.
import { buildTrack, trackAt, trackGap, speedLimitAt, laneFactorAt, lateralLimit, lateralStep, mod, LANE_W, LATERAL, ROAD_EDGE, type Track } from './track.js';
import { rollItem, ITEMS, type ItemId, type ItemKey, type ItemDef } from './items.js';
import { DIFFICULTY, MODES, isModeId, resolveRules, type ResolvedRules, type DifficultyId, type CustomItemDef } from './modes.js';
import { BIKE_KG, clamp, integrateSpeed, cdaFor, airFactor, postureStep, postureTarget, wakeShelter, pushShelter, combineShelter, shelterGrade } from './physics.js';
import { assistStep, assistManual, assistCalm, newAssist } from './assist.js';
import { botThink, makeBotAI, aimOf } from './bots.js';
import { afflict, applyCustom, hit, useItem, useCustom, pileUp, fallen, CRASH } from './race-items.js';
import { snapshot, applySnapshot } from './race-net.js';
import type {
  Look, HumanSpec, BotSpecialty, BotAI, Checkpoint, GhostRec, RaceOptions, Rider, Hazard, Shell, Box, RaceEvent, RaceResult, Snapshot, RaceState, TeamStanding } from './race-types.js';

// The data shapes live in race-types.ts; they stay importable from here.
export type { LateSpec, Look, HumanSpec, BotSpecialty, BotSpec, RaceRules, CheckpointDef, Checkpoint, GhostRec, RaceOptions, BotAI, Effects, Rider, Hazard, Shell, Box, RaceEvent, RaceResult, Snapshot, RaceState, TeamStanding } from './race-types.js';

/** Labels of the teams the engine forms itself (club teams carry the club's tag). */
export const TEAM_LABELS = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ'.split('');

/**
 * Crates of a row: three across the road, the row shifted by its distance so that rows do not line up. Derived from the
 * distance alone: a client rebuilds the same row as the server.
 */
export const crateRow = (s: number): number[] => { const shift = (((s * 0.618034) % 1) - 0.5) * 0.7; return [-1 + shift, shift, 1 + shift]; };
/** Riders in a race, humans and bots together: the room capacity online, the bot cap solo. */
export const MAX_RIDERS = 50;
export const MAX_BOTS = MAX_RIDERS - 1;
/** From this many riders the race starts rolling: a standing grid of 3 would stretch over 80 m. */
export const ROLLING_START_FROM = 13;
/** Neutralised speed of a rolling start (m/s), and the row spacing of the compact pack (m). */
export const ROLLING_V = 8, ROLLING_ROW = 2;
/** Starting grid: positions across the road of the rows of three and of four that alternate down the grid. */
export const GRID_ROWS: readonly (readonly number[])[] = [[-2 / 3, 0, 2 / 3], [-1, -1 / 3, 1 / 3, 1]];
/** One steering input moves the aim this far (about 45 cm), and the aim never runs further than `lead` ahead of the rider: holding the control is a steady move that stops on release. */
export const STEER = { nudge: 0.19, lead: 0.6 };
/** Seconds of countdown before the gun; a scheduled start begins its countdown this long before its time. */
export const START_COUNTDOWN = 3.999;
const COUNTDOWN = START_COUNTDOWN;

/**
 * Supporters' cheers (spectators, see Race.cheer). Every cheer is seen and heard; when the race allows the crowd
 * boost, a cheered rider also gets CHEER_BOOST on their power for CHEER_FX_S seconds, at most once every
 * CHEER_COOLDOWN_S seconds whatever the number of supporters: about half a percent of average power over a race,
 * a nudge the crowd can give, never a result it can decide.
 */
export const CHEER_KINDS = ['go', 'fire', 'clap', 'horn', 'heart', 'wow'] as const;
export type CheerKind = (typeof CHEER_KINDS)[number];
export const CHEER_BOOST = 1.03, CHEER_FX_S = 3, CHEER_COOLDOWN_S = 20;

export const BOT_NAMES = ['Steel Calves', 'The Rocket', 'Crazy Bottle', 'Derailleur', 'The Climber', 'Freewheel', 'Pedal Boat', 'Big Gear', 'Oiled Chain', 'Small Ring', 'The Puncheur',
  'Dancer', 'Mistral', 'Dynamo', 'Saddle Nose', 'Green Ray', 'Bear Paw', 'The Echelon', 'Tempo', 'The Metronome', 'Pedal Stroke', 'Blender', 'Fixie', 'Big Ring', 'Rear Wheel', 'Head of the Race',
  'The Attacker', 'Counterpunch', 'The Flame', 'Chain End', 'The Glider', 'White Collar', 'Windbreaker', 'Toe Clip', 'Leapfrog', 'The Hunter', 'Tailwind', 'Lucky Breakaway', 'The Roller', 'Rim Bottom'];
export const BOT_COLORS = ['#e74c3c', '#2ecc71', '#9b59b6', '#f39c12', '#1abc9c', '#e67e22', '#34495e', '#ff6fb5', '#8e44ad', '#16a085', '#d35400',
  '#c0392b', '#27ae60', '#2980b9', '#f1c40f', '#7f8c8d', '#e84393', '#00cec9', '#6c5ce7', '#fd79a8', '#55efc4', '#fab1a0', '#0984e3', '#b33939', '#218c74', '#ff793f',
  '#706fd3', '#33d9b2', '#cd6133', '#40407a', '#ffb142', '#2c2c54', '#ff5252', '#34ace0', '#ccae62', '#227093'];
/** Name and colour of the i-th bot of a race: past the list, the names come round with a numeral (a 50-rider field). */
export const botName = (i: number): string => BOT_NAMES[i % BOT_NAMES.length] + (i >= BOT_NAMES.length ? ` ${['II', 'III', 'IV', 'V'][Math.floor(i / BOT_NAMES.length) - 1] || Math.floor(i / BOT_NAMES.length) + 1}` : '');
export const botColor = (i: number): string => BOT_COLORS[i % BOT_COLORS.length];
const LOOKS = ['uni', 'bandes', 'degrade'];

function crossed(prev: number, cur: number, L: number, target: number): boolean {
  const d = cur - prev;
  if (d <= 0) return false;
  return mod(target - mod(prev, L), L) < d;
}

/**
 * An open road is ridden once, start to finish (ADR 0029): every mode becomes a run to the line there. No lap to
 * eliminate on, no endless road for a free ride or a timed game; the menus only offer the modes that fit
 * (fitsOpenRoad), this keeps the engine whole whatever it is handed.
 */
export const openRoadRules = (R: ResolvedRules): ResolvedRules => ({ ...R, finish: 'laps', laps: 1, eliminateLast: false, timeLimit: R.finish === 'timer' ? 0 : R.timeLimit });

export function mulberry(a: number): () => number {
  return () => { a |= 0; a = (a + 0x6d2b79f5) | 0; let t = Math.imul(a ^ (a >>> 15), 1 | a); t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t; return ((t ^ (t >>> 14)) >>> 0) / 4294967296; };
}

/**
 * Elastic group. Within ELASTIC_FREE metres of the group's middle a rider moves on their own watts, so holding a harder
 * target still shows; beyond it a pull of ELASTIC_PULL m/s per metre closes the gap. ELASTIC_MAX_CATCHUP bounds the pull
 * forward, for a rider coming back from a stop or joining late; ahead, a rider keeps at least 40 % of their own speed.
 * Below ELASTIC_MIN_W nobody is towed: a rider who stops pedalling stops, and the group does not wait for them.
 */
export const ELASTIC_FREE = 4, ELASTIC_PULL = 1, ELASTIC_MAX_CATCHUP = 12, ELASTIC_MIN_W = 10;

/** A gap in the clock (a network drop, a throttled tab) counts for OWN_MAX_GAP seconds at most: nothing was measured during it. */
export const OWN_MAX_GAP = 5;
/**
 * What a rider's watts alone cover on this track, for the recording of an elastic ride (ADR 0021). Distance gives XP,
 * totals and the weekly leaderboard, so the group must give none: neither the tow, nor the shelter of a group the rider
 * could not have stayed in, nor the slope of a place they were pulled to. It runs the road physics of Race.step with
 * no shelter and no effect, at its own position, and needs nothing from the network.
 */
export class OwnRide {
  s = 0; v = 0;
  private pose = 1; private last: number | null = null;
  constructor(private readonly track: Track, private readonly mass: number, private readonly cda: number, private readonly ftp: number) {}
  /** Rides with `power` up to the rider's clock `t` (s). */
  advance(t: number, power: number) {
    if (this.last != null && t > this.last) {
      const tr = this.track, step = 1 / 60;
      for (let left = Math.min(OWN_MAX_GAP, t - this.last); left > 1e-9; left -= step) {
        const dt = Math.min(step, left), pt = trackAt(tr, this.s);
        this.pose = postureStep(this.pose, postureTarget(pt.grade, this.v, power / Math.max(1, this.ftp)), dt);
        this.v = integrateSpeed(this.v, power, pt.grade, this.mass + BIKE_KG, this.cda * this.pose * airFactor(tr.alt0 + pt.y), dt, speedLimitAt(tr, this.s, 0));
        this.s += this.v * dt;
      }
    }
    this.last = this.last == null ? t : Math.max(this.last, t);
  }
}

export class Race {
  mode: string; rules: ResolvedRules; trackId: string; track: Track; laps: number; duration: number; items: boolean; ghost: GhostRec | null;
  seed: number; rnd: () => number; events: RaceEvent[]; t: number; state: RaceState;
  outCount: number; elimLap: number; finishTimer: number; hazards: Hazard[]; shells: Shell[]; ghostRec: GhostRec; lastRec: number;
  riders: Rider[]; human: Rider; boxes: Box[]; online = false; cps: Checkpoint[];
  /** Created items of this race, by key "c:<id>". */
  custom: Map<string, CustomItemDef>;
  /** Rolling start: the pack rolls neutralised at ROLLING_V during the countdown, in a compact formation. */
  rolling: boolean;
  /** Cheers may boost a rider (RaceOptions.crowdBoost, and only with items: the mode is already an arcade one). */
  crowdBoost: boolean;
  /** Elastic group (RaceOptions.elastic), only without a finish line. */
  elastic: boolean;

  constructor(o: RaceOptions = {}) {
    this.mode = o.mode || 'race';
    this.track = buildTrack(o.track || o.trackId || 'collines');
    this.trackId = this.track.id;
    const rules = resolveRules(o.rules, isModeId(this.mode) ? MODES[this.mode].rules : MODES.race.rules);
    const R = (this.rules = this.track.open ? openRoadRules(rules) : rules);
    this.laps = this.track.open ? 1 : R.finish === 'laps' ? o.laps || R.laps : R.finish === 'open' ? Infinity : 99;
    this.duration = R.timeLimit;
    this.items = R.items;
    this.custom = new Map(R.customItems.map(c => [`c:${c.id}`, c]));
    this.cps = (o.checkpoints || []).map(c => ({ ...c, order: [] }));
    this.ghost = R.ghost ? o.ghost || null : null;
    this.seed = o.seed ?? ((Date.now() & 0x7fffffff) >>> 0);
    this.rnd = mulberry(this.seed);
    this.events = [];
    this.t = -COUNTDOWN;
    this.state = 'countdown';
    this.outCount = 0; this.elimLap = 0; this.finishTimer = 0;
    this.hazards = []; this.shells = []; this.ghostRec = []; this.lastRec = -1;

    const humans = o.humans && o.humans.length ? o.humans : [o.human || { name: 'You', color: '#ffd400', mass: 75, ftp: 250 }];
    const wkg = humans.reduce((a, h) => a + (h.ftp || 250) / (h.mass || 75), 0) / humans.length;
    const diff = (DIFFICULTY[o.difficulty as DifficultyId] || DIFFICULTY.normal).factor;
    // the field never exceeds MAX_RIDERS: the bots fill what the humans leave
    const seats = Math.max(0, MAX_RIDERS - humans.length);
    const nBots = R.bots ? clamp(o.bots ?? 7, 0, seats) : 0;
    const riders = humans.map(h => this.makeRider({ pid: h.pid || 'local', key: h.key || h.pid || 'local', cat: h.cat ?? null, laps: h.laps, rookie: !!h.rookie, name: h.name || 'You', color: h.color || '#ffd400', human: true, mass: h.mass || 75, ftp: h.ftp || 250, look: h.look || null, assist: !!h.assist, tag: h.tag ?? null, country: h.country ?? null, team: h.team ?? null }));
    if (o.roster && nBots > 0) for (const b of o.roster.slice(0, seats)) riders.push(this.makeRider({ ...b, human: false, look: b.look || null }));
    else for (let i = 0; i < nBots; i++) {
      const mass = 62 + this.rnd() * 20;
      const ftp = R.pace === 'group' ? ((o.groupWkg || wkg * 0.75) * mass) / 0.88 : wkg * mass * diff * (0.92 + this.rnd() * 0.14);
      riders.push(this.makeRider({ key: 'bot' + i, name: botName(i), color: botColor(i), human: false, mass, ftp, look: { pattern: LOOKS[i % LOOKS.length], frame: 'couleur', helmet: 'couleur' } }));
    }
    riders.forEach((r, i) => { r.id = i; });
    const teams = R.teams ? this.formTeams(riders) : null, waves = teams && R.teams!.start === 'waves' ? teams : null;
    // starting grid: humans are spread through the pack
    const order = riders.filter(r => !r.human);
    riders.filter(r => r.human).forEach((h, k) => order.splice(Math.min(order.length, Math.floor(order.length / 2) + k * 2), 0, h));
    // a large field starts rolling: compact rows, already at speed, placed so the front row reaches the line at the gun
    this.rolling = waves ? false : (o.rollingStart ?? riders.length >= ROLLING_START_FROM);
    this.crowdBoost = !!o.crowdBoost && R.items;
    this.elastic = !!o.elastic && R.finish === 'open';
    const row = this.rolling ? ROLLING_ROW : 5, front = -2 - (this.rolling ? ROLLING_V * COUNTDOWN : 0);
    /** Grid positions of riders in start order: staggered rows of three and four across the road (a real grid), from
     *  `from` backwards. */
    const grid = (rs: Rider[], from: number) => {
      const out: { s: number; lane: number }[] = [];
      let n = 0, k = 0;
      for (let i = 0; i < rs.length; i++) { const slots = GRID_ROWS[n % 2]; out.push({ s: from - n * row, lane: slots[k] }); if (++k === slots.length) { k = 0; n++; } }
      return { out, rows: n + (k ? 1 : 0) };
    };
    if (waves) {
      // waves: each team on its own grid, the first at the line, the others queued behind it until their gun
      let behind = 0;
      waves.forEach((team, w) => {
        const g = grid(team, front), q = grid(team, front - behind * row);
        team.forEach((r, i) => {
          r.lane = r.targetLane = g.out[i].lane;
          if (w === 0) r.s = g.out[i].s;
          else { r.startT = w * R.teams!.waveGap; r.stageS = g.out[i].s; r.s = q.out[i].s; }
          r.prevS = r.s;
        });
        behind += g.rows + 1;
      });
    } else {
      const g = grid(order, front);
      order.forEach((r, i) => { r.s = g.out[i].s; r.lane = r.targetLane = g.out[i].lane; r.prevS = r.s; if (this.rolling) r.v = ROLLING_V; });
    }
    const ahead = R.finish === 'open' && Number.isFinite(o.startAt) ? clamp(o.startAt!, 0, 1e6) : 0;
    if (ahead > 0) for (const r of riders) { r.s += ahead; r.prevS = r.s; if (r.stageS != null) r.stageS += ahead; r.lapShown = this.lapOf(r); }
    this.riders = riders;
    this.human = riders[0];
    this.emit({ type: 'count', n: 3 });
    const rows = R.arena ? Array.from({ length: Math.floor(this.track.lap / 170) }, (_, i) => Math.round(60 + i * 170)) : this.track.boxRows;
    this.boxes = this.items ? rows.flatMap(s => crateRow(s).map(lane => ({ s, lane, cd: 0 }))) : [];
    for (const l of o.late || []) this.addHuman(l, l.startT);
  }

  /** Whether a rider may join this race in progress: a lap or open race, not an elimination, a timed game, a ghost run or a team race (teams are formed at the start). */
  get lateJoinable() { const R = this.rules; return (R.finish === 'laps' || R.finish === 'open') && !R.eliminateLast && !R.ghost && !R.teams; }

  /**
   * Forms the teams of a team mode, within each category (categories share the road but not the standings). Riders
   * who come with a team (their club) keep it; the other humans fill new teams of `size`; the bots then complete every
   * team of their category below `size` before forming their own, so club teams of two do not race teams of four. A
   * club present in two categories gives two teams, told apart by the category. Returns the teams in start order: the
   * humans' teams first, so the player never waits long.
   */
  private formTeams(riders: Rider[]): Rider[][] {
    const T = this.rules.teams!;
    interface Group { label: string; cat: string | null; club: boolean; riders: Rider[] }
    const groups = new Map<string, Group>(), placed = new Set<Rider>();
    const add = (g: Group, r: Rider) => { g.riders.push(r); placed.add(r); };
    const group = (cat: string | null, label: string, club = false) => { const k = `${cat ?? ''}|${label}`; let g = groups.get(k); if (!g) { g = { label, cat, club, riders: [] }; groups.set(k, g); } return g; };
    for (const r of riders) if (T.by === 'club' && r.human && r.team) add(group(r.cat, r.team, true), r); else r.team = null;
    const used = new Set([...groups.values()].map(g => g.label));
    let next = 0;
    const fresh = () => { let l: string; do { l = TEAM_LABELS[next % TEAM_LABELS.length] + (next >= TEAM_LABELS.length ? String(Math.floor(next / TEAM_LABELS.length) + 1) : ''); next++; } while (used.has(l)); used.add(l); return l; };
    // a team of the category with a seat left: for a human, never a club's team (a club rides with its own); for a bot, any
    const short = (cat: string | null, bot: boolean) => [...groups.values()].find(g => g.cat === cat && g.riders.length < T.size && (bot || !g.club));
    for (const r of riders) if (r.human && !placed.has(r)) add(short(r.cat, false) ?? group(r.cat, fresh()), r);
    for (const r of riders) if (!r.human) add(short(r.cat, true) ?? group(r.cat, fresh()), r);
    // the team id is the label, suffixed by the category only when the same label exists in several categories
    const labels = new Map<string, number>();
    for (const g of groups.values()) labels.set(g.label, (labels.get(g.label) ?? 0) + 1);
    for (const g of groups.values()) { const id = (labels.get(g.label) ?? 0) > 1 && g.cat ? `${g.label} ${g.cat}` : g.label; for (const r of g.riders) r.team = id; }
    const list = [...groups.values()].map(g => g.riders);
    return [...list.filter(rs => rs.some(r => r.human)), ...list.filter(rs => !rs.some(r => r.human))];
  }

  /**
   * A rider joins the race in progress: at the line, after the field, with their own countdown (COUNTDOWN seconds from now,
   * or the `startT` the server decided) and their own clock, so they ride the whole course and their time counts from their gun.
   */
  addHuman(h: HumanSpec, startT?: number): Rider {
    const r = this.makeRider({ pid: h.pid || 'local', key: h.key || h.pid || 'local', cat: h.cat ?? null, laps: h.laps, rookie: !!h.rookie, name: h.name || 'You', color: h.color || '#ffd400', human: true, mass: h.mass || 75, ftp: h.ftp || 250, look: h.look || null, assist: !!h.assist, tag: h.tag ?? null, country: h.country ?? null, team: h.team ?? null });
    r.id = this.riders.length; r.s = r.prevS = -2; r.startT = startT ?? +(this.t + COUNTDOWN).toFixed(3);
    this.riders.push(r);
    this.emit({ type: 'joined', who: r.id });
    this.emit({ type: 'count', n: 3, who: r.id });
    return r;
  }

  /** A rider's own race clock: seconds since their gun (the field's, or theirs if they joined in progress). */
  clock(r: Rider) { return Math.max(0, this.t - r.startT); }

  makeRider(p: Omit<HumanSpec, 'look' | 'color'> & { color: string; human: boolean; look: Look | null; spec?: BotSpecialty }): Rider {
    const { spec, laps, ...rest } = p;
    return {
      ...rest, key: p.key || p.pid || 'local', cat: p.cat ?? null, rookie: !!p.rookie, goal: this.lapped() && laps && !this.track.open ? clamp(Math.round(laps), 1, 50) : this.laps,
      tag: p.tag ?? null, country: p.country ?? null, team: p.team ?? null, stageS: null,
      id: 0, s: 0, prevS: 0, v: 0, lane: 0, targetLane: 0, latV: 0, power: 0, cadence: 0, hr: 0, gear: 7,
      item: null, itemRoll: 0, fx: {}, shield: 0, invuln: 0, draft: 0, bombMult: 1, chargeSum: 0, chargeT: 0,
      finishT: null, out: false, outOrder: 0, hits: 0, itemsUsed: 0, lapShown: 1, gone: false, startT: 0, cheerCd: 0,
      cda: cdaFor(p.mass), pose: 1,
      ai: p.human ? null : this.makeAI(p.mass, p.ftp, spec),
      assist: p.human && p.assist ? newAssist() : null,
    };
  }

  /** Lap-based race (finish rule "laps"): each rider has their own lap goal. */
  lapped() { return this.rules.finish === 'laps'; }
  emit(e: RaceEvent) { this.events.push(e); }
  drain() { const e = this.events; this.events = []; return e; }

  // ---- standings ----
  standings() {
    const sc = this.rules.scoring;
    return [...this.riders].sort((a, b) => {
      if (a.out !== b.out) return a.out ? 1 : -1;
      if (a.out && b.out) return b.outOrder - a.outOrder;
      if (sc === 'hits' && a.hits !== b.hits) return b.hits - a.hits;
      if (a.finishT != null || b.finishT != null) { if (a.finishT == null) return 1; if (b.finishT == null) return -1; return a.finishT - b.finishT; }
      return b.s - a.s;
    });
  }
  rankOf(r: Rider) { return this.standings().indexOf(r) + 1; }

  /**
   * Team standings of a team mode (empty otherwise): a team's time is its nth rider's, its best rider's, or the sum of
   * its riders' places ("score", lower is better). Scored teams come first; the others are ranked by the pace of the
   * rider who counts (metres per second of their own clock), which compares teams whatever wave they started in.
   */
  teamStandings(): TeamStanding[] {
    const T = this.rules.teams;
    if (!T) return [];
    const st = this.standings(), groups = new Map<string, Rider[]>();
    for (const r of st) if (r.team) (groups.get(r.team) ?? groups.set(r.team, []).get(r.team)!).push(r);
    const rows = [...groups.entries()].map(([team, rs]): Omit<TeamStanding, 'rank'> => {
      const riders = rs.map(r => r.id);
      if (T.scoring === 'ranks') {
        const score = rs.reduce((a, r) => a + st.indexOf(r) + 1, 0), done = rs.every(r => r.finishT != null || r.out);
        return { team, riders, time: null, pace: 0, score, done };
      }
      const n = T.scoring === 'best' ? 1 : Math.min(T.nth, rs.length);
      const finished = rs.filter(r => r.finishT != null && !r.out).sort((a, b) => a.finishT! - b.finishT!);
      const time = finished.length >= n ? finished[n - 1].finishT : null;
      // the rider who counts: the nth best placed, by distance covered per second of their own clock
      const counting = [...rs].filter(r => !r.out).sort((a, b) => b.s - a.s)[n - 1] ?? rs[rs.length - 1];
      const pace = counting && this.t > counting.startT ? Math.max(0, counting.s) / Math.max(1, this.clock(counting)) : 0;
      return { team, riders, time, pace, score: null, done: time != null };
    });
    rows.sort((a, b) => {
      if (a.score != null && b.score != null) return a.score - b.score;
      if (a.time != null || b.time != null) { if (a.time == null) return 1; if (b.time == null) return -1; return a.time - b.time; }
      return b.pace - a.pace;
    });
    return rows.map((r, i) => ({ ...r, rank: i + 1 }));
  }
  /** The standing of a rider's team, or null outside team modes. */
  teamOf(r: Rider): TeamStanding | null { return r.team ? this.teamStandings().find(ts => ts.team === r.team) ?? null : null; }
  /** An open road ends: its run-out brakes riders to a stop, this holds the last centimetres of an integration step. */
  private roadEnd(r: Rider) {
    const end = this.track.lap + this.track.run;
    if (this.track.open && r.s > end) { r.s = end; r.v = 0; }
  }
  lapOf(r: Rider) { return clamp(Math.floor(Math.max(0, r.s) / this.track.lap) + 1, 1, r.goal === Infinity ? 9999 : r.goal); }
  active() { return this.riders.filter(r => !r.out); }
  humans() { return this.riders.filter(r => r.human && !r.gone); }
  byPid(pid: string) { return this.riders.find(r => r.pid === pid); }

  /** Simulated gradient sent to rider r's trainer (in %). */
  trainerGrade(r: Rider = this.human, difficulty = 0.5): number {
    // on the ground nobody is on the bike: the road's slope and the effects no longer reach the legs
    if (r.fx.crash) return 0;
    const g = trackAt(this.track, r.s).grade * 100 * difficulty;
    const f = r.fx;
    let off = (r.gear - 7) * 0.9;
    if (f.turbo) off -= 4;
    if (f.bomb) off -= 3;
    if (f.heavy) off += 5;
    if (f.spin) off += 3;
    if (f.headwind) off += 3;
    if (f.cheer) off -= 1; // the crowd carries you a little
    for (const d of this.activeCustom(r)) off += d.content.effect.grade ?? 0;
    // in the shelter, the legs feel the drag saved, at the same dosage as the gradient
    off -= shelterGrade(r.v, r.draft, r.mass + BIKE_KG, r.cda * r.pose) * 100 * difficulty;
    return clamp(g + off, -10, 20);
  }

  shiftGear(r: Rider, d: number) {
    if (r.fx.jam) { this.emit({ type: 'deny', who: r.id }); return; }
    r.gear = clamp(r.gear + d, 1, 12);
  }
  /**
   * A human moves their aim across the road, by a small step per input: the position is free, from one edge of the road
   * to the other, and small enough steps let a rider place themselves beside any wheel.
   */
  steer(r: Rider, d: number) {
    if (r.out || r.fx.crash) return;
    const aim = clamp(r.targetLane + d * STEER.nudge, r.lane - STEER.lead, r.lane + STEER.lead);
    r.targetLane = clamp(aim, -ROAD_EDGE, ROAD_EDGE);
    if (r.assist) assistManual(r.assist);
  }
  addFx(r: Rider, k: string, t: number) { r.fx[k] = Math.max(r.fx[k] || 0, t); }

  /**
   * A supporter cheers for rider r: everyone hears it (event `cheer`), and when the race allows the crowd boost and
   * the rider's cooldown is over, their legs feel it for a moment. Returns whether the boost was given.
   */
  cheer(r: Rider, kind: CheerKind, from: string): boolean {
    const boosted = this.crowdBoost && this.state === 'running' && !r.out && r.finishT == null && r.cheerCd <= 0;
    if (boosted) { this.addFx(r, 'cheer', CHEER_FX_S); r.cheerCd = CHEER_COOLDOWN_S; }
    this.emit({ type: 'cheer', who: r.id, kind, from, boost: boosted });
    return boosted;
  }

  /** Description of an item for the UI: official or created. */
  itemInfo(key: string): ItemDef {
    const c = this.custom.get(key);
    return c ? { icon: c.content.icon, name: c.name, help: c.help || '' } : ITEMS[key as ItemId] || { icon: '❔', name: key, help: '' };
  }
  /** Created items whose effect is active on this rider. */
  private activeCustom(r: Rider): CustomItemDef[] {
    if (!this.custom.size) return [];
    const out: CustomItemDef[] = [];
    for (const k in r.fx) if (k.startsWith('c:')) { const d = this.custom.get(k); if (d) out.push(d); }
    return out;
  }
  /** Wind felt (m/s) on rider r's trainer, added by created items. */
  trainerWind(r: Rider = this.human): number {
    let w = 0;
    for (const d of this.activeCustom(r)) w += d.content.effect.wind ?? 0;
    return clamp(w, -8, 15);
  }

  /** A player leaves the game in progress: a bot takes over their bike. */
  dropPlayer(pid: string) {
    const r = this.byPid(pid);
    if (!r || !r.human) return;
    r.human = false; r.gone = true; r.assist = null; r.ai = this.makeAI(r.mass, r.ftp, undefined, false);
    this.emit({ type: 'left', who: r.id });
  }

  eliminate(r: Rider, lap?: number) {
    if (r.out) return;
    r.out = true; r.outOrder = ++this.outCount; r.item = null; r.itemRoll = 0;
    this.emit({ type: 'elim', who: r.id, lap });
  }

  /** Nearest rider ahead on the road within `maxGap` metres; `ok` narrows the candidates (teammates, for one). */
  nearestAhead(r: { s: number }, maxGap = Infinity, ok?: (o: Rider) => boolean): Rider | null {
    let best: Rider | null = null, bg = maxGap;
    for (const o of this.riders) {
      if (o === r || o.out || o.finishT != null || (ok && !ok(o))) continue;
      const g = o.s - r.s;
      if (g > 0 && g < bg) { bg = g; best = o; }
    }
    return best;
  }
  leaderExcept(r: Rider): Rider | null { return this.standings().find(o => o !== r && !o.out && o.finishT == null) || null; }

  nearestHuman(r: Rider): Rider | null {
    let best: Rider | null = null, bg = Infinity;
    for (const h of this.riders) if (h.human && !h.out && h.finishT == null) { const g = Math.abs(h.s - r.s); if (g < bg) { bg = g; best = h; } }
    return best;
  }

  /** Immediately ends an open game (free ride, workout). */
  finishNow() {
    for (const h of this.humans()) if (h.finishT == null) h.finishT = this.clock(h);
    this.state = 'done';
    this.emit({ type: 'done' });
  }

  /**
   * Pulls the riders towards the humans' median position: one rider far behind (a late joiner, someone back from a stop)
   * does not drag a whole group back. Bots follow the humans; they never count in the median.
   */
  private elasticStep(dt: number) {
    const rolling = (r: Rider) => !r.out && !r.fx.crash && this.t >= r.startT && (!r.human || r.power >= ELASTIC_MIN_W);
    const at = this.riders.filter(r => r.human && !r.gone && rolling(r)).map(r => r.s).sort((a, b) => a - b);
    if (!at.length) return;
    const mid = (at[(at.length - 1) >> 1] + at[at.length >> 1]) / 2;
    for (const r of this.riders) {
      if (!rolling(r)) continue;
      const d = r.s - mid;
      if (Math.abs(d) <= ELASTIC_FREE) continue;
      r.s += clamp(-(d - Math.sign(d) * ELASTIC_FREE) * ELASTIC_PULL, -0.6 * r.v, ELASTIC_MAX_CATCHUP) * dt;
    }
  }

  // ---- simulation step ----
  step(dt: number) {
    if (this.state === 'done') return;
    const prevT = this.t;
    this.t += dt;
    if (this.state === 'countdown') {
      // rolling start: the whole pack rolls neutralised, keeping its formation, until the gun
      if (this.rolling) for (const r of this.riders) { r.prevS = r.s; r.s += ROLLING_V * dt; r.v = ROLLING_V; }
      const n = -Math.ceil(this.t);
      if (n > 0 && Math.ceil(this.t) !== Math.ceil(prevT)) this.emit({ type: 'count', n });
      if (this.t >= 0) { this.state = 'running'; this.t = 0; this.emit({ type: 'count', n: 0 }); }
      return;
    }
    const tr = this.track, L = tr.L;
    // a rider who joined in progress, or a later wave, has their own countdown, shown to them alone (`who`), same beats as the field's
    for (const r of this.riders) if (r.startT > 0 && r.startT > prevT) {
      const tt = this.t - r.startT, ptt = prevT - r.startT;
      const n = -Math.ceil(tt);
      if (n > 0 && Math.ceil(tt) !== Math.ceil(ptt)) this.emit({ type: 'count', n, who: r.id });
      if (tt >= 0) {
        this.emit({ type: 'count', n: 0, who: r.id });
        // a wave leaves the queue for the grid the first wave left: every team rides the same distance
        if (r.stageS != null) { r.s = r.prevS = r.stageS; r.stageS = null; }
      }
    }

    for (const r of this.riders) {
      if (r.out) continue;
      for (const k of Object.keys(r.fx)) {
        r.fx[k]! -= dt;
        if (r.fx[k]! <= 0) {
          delete r.fx[k];
          if (k === 'charge') {
            const avg = r.chargeT > 0 ? r.chargeSum / r.chargeT : 0;
            r.bombMult = 1 + clamp((avg / r.ftp - 0.7) * 1.2, 0.15, 1.3);
            this.addFx(r, 'bomb', 3);
            this.emit({ type: 'bomb', who: r.id, avg: Math.round(avg), pct: Math.round((avg / r.ftp) * 100), boost: Math.round((r.bombMult - 1) * 100) });
          }
        }
      }
      if (r.fx.charge) { r.chargeSum += r.power * dt; r.chargeT += dt; }
      if (r.shield > 0) r.shield = Math.max(0, r.shield - dt);
      if (r.invuln > 0) r.invuln -= dt;
      if (r.cheerCd > 0) r.cheerCd -= dt;
      if (r.itemRoll > 0) {
        r.itemRoll -= dt;
        if (r.itemRoll <= 0) {
          r.itemRoll = 0;
          const act = this.active(), n = act.length;
          r.item = rollItem(n > 1 ? (this.rankOf(r) - 1) / (n - 1) : 0.5, { arena: this.rules.arena, pool: this.rules.itemPool, boost: this.rules.itemBoost, custom: this.rules.customItems, builtin: this.rules.builtinItems, field: n }, this.rnd);
          this.emit({ type: 'item', who: r.id, item: r.item });
        }
      }
      if (r.fx.crash) { if (!r.human) { r.power = 0; r.cadence = 0; } continue; }
      if (!r.human) this.botThink(r, dt);
      else if (r.assist && this.rules.draft) {
        // the assist does not swerve, but never picks the line of a fallen rider nor takes their wheel
        const down = fallen(this);
        const lane = down.length
          ? assistStep(r.assist, r, this.riders.filter(o => !o.fx.crash), [...this.hazards, ...down], L, dt)
          : assistStep(r.assist, r, this.riders, this.hazards, L, dt);
        if (lane != null) r.targetLane = lane;
      }
    }

    // drafting
    for (const r of this.riders) {
      if (r.out || r.fx.crash) { r.draft = 0; continue; }
      if (!this.rules.draft) { r.draft = r.fx.magnet ? 0.38 : 0; continue; }
      const parts: number[] = [];
      let lead = 0;
      const mates = this.rules.teams?.draftTeamOnly;
      for (const o of this.riders) {
        if (o === r || o.out || o.fx.crash || (mates && o.team !== r.team)) continue;
        // physical gap along the loop: a rider lapped once is still shelter if just ahead
        const gap = trackGap(tr, r.s, o.s), side = (o.lane - r.lane) * LANE_W;
        if (gap > 0) parts.push(wakeShelter(gap, side));
        else lead = Math.max(lead, pushShelter(-gap, side));
      }
      r.draft = Math.max(r.fx.magnet ? 0.38 : 0, combineShelter(parts, lead));
      for (const c of this.activeCustom(r)) r.draft = Math.max(r.draft, c.content.effect.draft ?? 0);
    }

    // physics
    for (const r of this.riders) {
      if (r.out) continue;
      r.prevS = r.s;
      if (this.t < r.startT) { r.v = 0; continue; } // held at the line until their own gun
      const f = r.fx;
      // down: the watts no longer reach the road; the rider slides to a stop and stays there until back on the bike
      if (f.crash) {
        r.v = r.v > CRASH.stop ? r.v * Math.exp(-CRASH.brake * dt) : 0;
        r.s += r.v * dt; r.latV = 0; r.targetLane = r.lane;
        this.roadEnd(r);
        continue;
      }
      const pt = trackAt(tr, r.s), g = pt.grade;
      let pm = 1;
      if (f.turbo) pm *= 1.5;
      if (f.bomb) pm *= r.bombMult;
      if (f.heavy) pm *= 0.75;
      if (f.spin) pm *= 0.4;
      if (f.cheer) pm *= CHEER_BOOST;
      if (!r.human && f.jam) pm *= 0.9;
      if (!r.human && f.fog) pm *= 0.93;
      let drag = 1;
      for (const c of this.activeCustom(r)) { pm *= c.content.effect.power ?? 1; drag *= c.content.effect.drag ?? 1; }
      r.pose = postureStep(r.pose, postureTarget(g, r.v, r.power / Math.max(1, r.ftp)), dt);
      const cda = r.cda * r.pose * airFactor(tr.alt0 + pt.y) * (f.headwind ? 2.2 : 1) * drag * (1 - r.draft);
      // chord: inside a corner the path is shorter but the radius tighter
      const off = r.lane * LANE_W;
      r.v = integrateSpeed(r.v, r.power * pm, g, r.mass + BIKE_KG, cda, dt, speedLimitAt(tr, r.s, off));
      r.s += (r.v * dt) / laneFactorAt(tr, r.s, off);
      this.roadEnd(r);
      const calm = !!r.ai || assistCalm(r.assist); // only a hand on the bars, or a pass, moves at a human's full angle
      const moved = lateralStep(r.lane, r.latV, aimOf(r), dt, calm ? LATERAL.botGain : LATERAL.gain, lateralLimit(r.v, !calm));
      r.lane = moved.lane; r.latV = moved.speed;
    }
    if (this.elastic) this.elasticStep(dt);
    pileUp(this);

    // boxes
    for (const b of this.boxes) {
      if (b.cd > 0) { b.cd -= dt; continue; }
      for (const r of this.riders) {
        if (r.out || r.item || r.itemRoll || r.finishT != null || r.fx.crash) continue;
        if (Math.abs(r.lane - b.lane) < 0.7 && crossed(r.prevS, r.s, L, b.s)) {
          b.cd = this.rules.arena ? 1.5 : 3;
          r.itemRoll = 1.1;
          this.emit({ type: 'roll', who: r.id });
          break;
        }
      }
    }

    // bananas
    for (const z of this.hazards) z.age += dt;
    this.hazards = this.hazards.filter(z => {
      if (z.age > 60) return false;
      for (const r of this.riders) {
        if (r.out || (r === z.owner && z.age < 1)) continue;
        if (Math.abs(r.lane - z.lane) < 0.6 && crossed(r.prevS, r.s, L, mod(z.s, L))) { this.hit(r, z.item ?? 'banana', z.owner === r ? null : z.owner); return false; }
      }
      return true;
    });

    // missiles
    this.shells = this.shells.filter(sh => {
      sh.life -= dt;
      if (sh.life <= 0) return false;
      if (sh.target && (sh.target.out || sh.target.finishT != null)) sh.target = this.nearestAhead({ s: sh.s });
      const prev = sh.s, step = 34 * dt;
      sh.s += step; sh.dist += step;
      if (sh.target) { const dl = sh.target.lane - sh.lane; sh.lane += Math.sign(dl) * Math.min(Math.abs(dl), 3 * dt); }
      for (const r of this.riders) {
        if (r === sh.owner || r.out || r.finishT != null) continue;
        const near = r === sh.target ? 0.9 : 0.55;
        if (r.s >= prev - 0.5 && r.s <= sh.s + 0.5 && Math.abs(r.lane - sh.lane) < near) { this.hit(r, sh.item ?? 'shell', sh.owner); return false; }
      }
      return !(!sh.target && sh.dist > 250);
    });

    // checkpoints (sprints, summits): crossing order, tie-broken by the estimated instant of passing
    for (let ci = 0; ci < this.cps.length; ci++) {
      const c = this.cps[ci];
      const cross = this.riders.filter(r => !r.out && r.prevS < c.s && r.s >= c.s && !c.order.includes(r.id))
        .sort((a, b) => (c.s - a.prevS) / (a.s - a.prevS || 1) - (c.s - b.prevS) / (b.s - b.prevS || 1));
      for (const r of cross) { c.order.push(r.id); this.emit({ type: 'cp', who: r.id, cp: ci, kind: c.kind, rank: c.order.length, label: c.label }); }
    }

    // laps, finish
    const R = this.rules, lapped = R.finish === 'laps';
    for (const r of this.riders) {
      if (r.out || r.finishT != null) continue;
      const lap = this.lapOf(r);
      if (lap !== r.lapShown) { r.lapShown = lap; if (r.human) this.emit({ type: 'lap', who: r.id, lap, laps: r.goal }); }
      if (lapped && r.s >= r.goal * tr.lap) {
        r.finishT = this.clock(r);
        this.emit({ type: 'finish', who: r.id, rank: this.rankOf(r), time: r.finishT });
      }
    }
    if (R.eliminateLast) {
      const act = this.active();
      const leaderLaps = Math.floor(Math.max(...act.map(r => r.s)) / tr.lap);
      if (leaderLaps > this.elimLap && act.length > 1) {
        this.elimLap = leaderLaps;
        this.eliminate(act.reduce((m, o) => (o.s < m.s ? o : m)), leaderLaps);
        if (this.active().length > 1) this.emit({ type: 'round', lap: leaderLaps + 1, left: this.active().length });
      }
    }

    if (R.ghost && this.t - this.lastRec >= 0.25 && this.human.finishT == null) { this.lastRec = this.t; this.ghostRec.push([+this.t.toFixed(2), +this.human.s.toFixed(1)]); }

    // end of game: when all humans still connected have finished
    const hs = this.humans();
    const doneH = hs.length === 0 || hs.every(h => h.finishT != null || h.out);
    const allIn = this.riders.every(r => r.finishT != null || r.out);
    let over = false;
    // a team race waits for the humans' teams to be scored (their nth rider in), not for the humans alone
    const teamsIn = R.teams ? (() => { const ts = this.teamStandings(); return hs.length === 0 || hs.every(h => ts.find(x => x.team === h.team)?.done); })() : false;
    if (R.finish === 'laps') over = R.ghost ? this.human.finishT != null : R.teams ? teamsIn || allIn : doneH || allIn;
    else if (R.finish === 'survivor') over = doneH || this.active().length <= 1;
    else if (R.finish === 'timer') over = doneH || this.active().length <= 1 || this.t >= this.duration;
    if (this.duration && this.t >= this.duration) over = true; // a mode's time cap, including an open game
    if (over) {
      this.finishTimer += dt;
      const race = R.finish === 'laps' && !R.ghost;
      if (this.finishTimer >= (race ? 8 : 3) || (race && allIn)) {
        this.state = 'done';
        if (R.ghost && this.human.finishT != null) this.ghostRec.push([+this.human.finishT.toFixed(2), +(this.laps * tr.lap).toFixed(1)]);
        this.emit({ type: 'done' });
      }
    }
  }

  ghostS(t: number): number | null {
    const g = this.ghost;
    if (!g || !g.length) return null;
    if (t <= g[0][0]) return g[0][1];
    let lo = 0, hi = g.length - 1;
    if (t >= g[hi][0]) return g[hi][1];
    while (hi - lo > 1) { const mid = (lo + hi) >> 1; if (g[mid][0] <= t) lo = mid; else hi = mid; }
    const f = (t - g[lo][0]) / (g[hi][0] - g[lo][0]);
    return g[lo][1] + (g[hi][1] - g[lo][1]) * f;
  }

  results(): RaceResult[] {
    const teams = this.teamStandings();
    return this.standings().map((r, i) => {
      const ts = r.team ? teams.find(x => x.team === r.team) : null;
      return {
        rank: i + 1, id: r.id, pid: r.pid || null, key: r.key, cat: r.cat, rookie: r.rookie, name: r.name, human: r.human || !!r.pid, color: r.color,
        time: r.finishT, dist: r.s, goal: r.goal === Infinity || !this.lapped() ? null : r.goal * this.track.lap, out: r.out, hits: r.hits,
        tag: r.tag, country: r.country, team: r.team, teamRank: ts?.rank ?? null, teamTime: ts?.time ?? null,
      };
    });
  }

  // ---- bots, items and network code live in sibling modules (bots, race-items, race-net) ----
  botThink(r: Rider, dt: number) { return botThink(this, r, dt); }
  private makeAI(mass: number, ftp: number, spec?: BotSpecialty, rnd = true): BotAI { return makeBotAI(this, mass, ftp, spec, rnd); }
  afflict(target: Rider | null, src: Rider | null, fn: (r: Rider) => void, what: string): boolean { return afflict(this, target, src, fn, what); }
  applyCustom(t: Rider, d: CustomItemDef) { return applyCustom(this, t, d); }
  hit(target: Rider, kind: 'banana' | 'shell' | ItemKey, src: Rider | null | undefined) { return hit(this, target, kind, src); }
  useItem(r: Rider): boolean { return useItem(this, r); }
  useCustom(r: Rider, d: CustomItemDef, ev: RaceEvent & { targets?: number[] }) { return useCustom(this, r, d, ev); }
  snapshot(): Snapshot { return snapshot(this); }
  applySnapshot(S: Snapshot, a = 1, prev: Snapshot | null = null) { return applySnapshot(this, S, a, prev); }
}

export function fmtTime(t: number | null | undefined): string {
  if (t == null || !isFinite(t)) return '—';
  const h = Math.floor(t / 3600), m = Math.floor((t % 3600) / 60), s = t - h * 3600 - m * 60;
  return h ? `${h}:${String(m).padStart(2, '0')}:${String(Math.floor(s)).padStart(2, '0')}` : `${m}:${s.toFixed(1).padStart(4, '0')}`;
}
